import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2,
  Trophy,
  Star,
  Heart,
  Coins,
  ArrowRight,
  Flame,
  Globe2,
  Mic2,
  PlayCircle,
} from "lucide-react";

/**
 * Plyx Stars — BGT / Voice-style talent-show hub at /plyx/talent.
 *
 * Renders three things from /api/plyx/talent:
 *   1. Active talent contests (live + judging)
 *   2. Country-vs-country leaderboard (last 30 days of talent entries)
 *   3. Judge's Pick standout performances (🏆)
 *
 * Public (no auth required) so share links work for everyone. Voting
 * happens on the contest detail page via the existing challenge flow
 * (hearts free + gifts paid).
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: false });

const _fmtCoins = (n) => (n || 0).toLocaleString();
const _fmtWhen = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
};

const RankBadge = ({ rank }) => {
  const bg =
    rank === 1 ? "linear-gradient(135deg,#FFD86B,#FFB42D)" :
    rank === 2 ? "linear-gradient(135deg,#E0E0E0,#A8A8A8)" :
    rank === 3 ? "linear-gradient(135deg,#E0915C,#C05A3C)" :
    "rgba(255,255,255,0.06)";
  const color = rank <= 3 ? "#0A0A0A" : "rgba(255,255,255,0.75)";
  return (
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
      style={{ background: bg, color }}
    >
      {rank}
    </div>
  );
};

const PlyxTalent = () => {
  const [data, setData] = useState(null);
  const [seasonData, setSeasonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Plyx Stars — Where the world's next stars rise";
    Promise.all([
      publicApi.get("/plyx/talent"),
      publicApi.get("/plyx/seasons/active").catch(() => ({ data: { season: null } })),
    ])
      .then(([t, s]) => {
        setData(t.data);
        setSeasonData(s.data?.season || null);
      })
      .catch((e) => setError(e.response?.data?.detail || "Could not load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin opacity-60" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
        <div className="text-center text-white/55">{error || "Could not load Plyx Stars"}</div>
      </div>
    );
  }

  const { active = [], country_leaderboard = [], judges_picks = [] } = data;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24" data-testid="plyx-talent-page">
      {/* Hero */}
      <div
        className="relative px-6 sm:px-10 py-14 sm:py-20 border-b border-white/5 overflow-hidden"
        style={{
          background:
            "radial-gradient(circle at 20% 0%, rgba(255,45,122,0.18) 0%, transparent 55%)," +
            "radial-gradient(circle at 85% 30%, rgba(0,224,199,0.14) 0%, transparent 60%)",
        }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold text-white mb-5"
            style={{ background: "linear-gradient(135deg,#FF2D7A,#FFB42D,#00E0C7)" }}>
            <Mic2 className="w-3 h-3" />{" "}
            {seasonData
              ? `${seasonData.brand} · ${seasonData.current_round_name || "Live"}`
              : "Plyx Stars · Season Live"}
          </div>
          <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[0.95]" data-testid="plyx-talent-hero">
            Where the world&apos;s
            <br />
            <span
              style={{
                background: "#E85D4E",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              next stars
            </span>
            {" "}rise.
          </h1>
          <p className="mt-6 text-lg text-white/65 max-w-2xl leading-relaxed">
            Sing, dance, perform — your country backs you, the crowd votes, the diaspora wins.
            Drop a 60-second clip in any live contest below and let the world decide.
          </p>
          <div className="mt-7 flex gap-3 flex-wrap">
            <Link
              to="/plyx/upload"
              data-testid="plyx-talent-enter-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold text-white hover:scale-[1.02] transition-transform"
              style={{ background: "linear-gradient(135deg,#FF2D7A,#FFB42D,#00E0C7)" }}
            >
              <Star className="w-4 h-4 fill-current" /> Enter a contest
            </Link>
            <Link
              to="/plyx/feed"
              data-testid="plyx-talent-watch-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-white/15 hover:bg-white/5"
            >
              <PlayCircle className="w-4 h-4" /> Watch the feed
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 sm:px-10 mt-12 grid lg:grid-cols-3 gap-10">
        {/* LEFT: Active contests + Judge's Picks */}
        <div className="lg:col-span-2 space-y-10">
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl flex items-center gap-2">
                <Flame className="w-5 h-5 text-[#FF2D7A]" /> Live contests
              </h2>
              <span className="text-xs text-white/45">{active.length} running</span>
            </div>
            {active.length === 0 ? (
              <div className="p-6 rounded-xl border border-dashed border-white/10 text-sm text-white/55 text-center" data-testid="plyx-talent-no-contests">
                No contests running right now. New round drops weekly — come back Monday.
              </div>
            ) : (
              <ul className="space-y-3" data-testid="plyx-talent-active-list">
                {active.map((c) => (
                  <li key={c.challenge_id}>
                    <Link
                      to={`/plyx/challenges/${c.challenge_id}`}
                      data-testid={`plyx-talent-contest-${c.challenge_id}`}
                      className="block p-5 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="font-display text-lg leading-tight">{c.title}</h3>
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider shrink-0"
                          style={{
                            background: c.status === "live" ? "#FF2D7A" : "rgba(255,255,255,0.1)",
                            color: c.status === "live" ? "#fff" : "rgba(255,255,255,0.75)",
                          }}
                        >
                          {c.status}
                        </span>
                      </div>
                      {c.description && (
                        <p className="text-sm text-white/55 leading-snug mb-3 line-clamp-2">{c.description}</p>
                      )}
                      <div className="flex flex-wrap gap-4 text-xs text-white/60">
                        <span><strong className="text-white">{c.entry_count || 0}</strong> entries</span>
                        <span className="inline-flex items-center gap-1">
                          <Heart className="w-3 h-3" /> {_fmtCoins(c.total_hearts)} hearts
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Coins className="w-3 h-3 text-[#FFB42D]" /> {_fmtCoins(c.total_gift_coins)} gifts
                        </span>
                        {c.prize_pool_coins > 0 && (
                          <span className="inline-flex items-center gap-1 text-white/85 font-semibold">
                            <Trophy className="w-3 h-3" /> {_fmtCoins(c.prize_pool_coins)} prize pool
                          </span>
                        )}
                        <span className="ml-auto text-white/40">Ends {_fmtWhen(c.ends_at)}</span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {judges_picks.length > 0 && (
            <section>
              <h2 className="font-display text-2xl flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-[#FFB42D]" /> Judge&apos;s Picks
              </h2>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="plyx-talent-judges-picks">
                {judges_picks.map((p) => (
                  <Link
                    key={p.entry_id}
                    to={`/plyx/challenges/${p.challenge_id}`}
                    data-testid={`plyx-talent-pick-${p.entry_id}`}
                    className="flex items-start gap-3 p-4 rounded-xl border border-[#FFB42D]/25 bg-[#FFB42D]/5 hover:bg-[#FFB42D]/10 transition-colors"
                  >
                    {p.thumbnail_url ? (
                      <img src={p.thumbnail_url} alt="" className="w-16 h-20 object-cover rounded-md shrink-0" />
                    ) : (
                      <div className="w-16 h-20 rounded-md bg-white/5 flex items-center justify-center shrink-0">
                        <Trophy className="w-5 h-5 text-[#FFB42D]" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] uppercase tracking-wider text-[#FFB42D] font-bold mb-1">
                        🏆 Judge&apos;s Pick
                      </div>
                      <div className="text-sm font-semibold truncate">
                        @{p.creator?.handle} · {p.creator?.country || "—"}
                      </div>
                      <div className="text-xs text-white/55 line-clamp-2 mt-0.5">{p.caption || p.challenge_title}</div>
                      <div className="text-[11px] text-white/45 mt-1.5 flex gap-3">
                        <span>♥ {_fmtCoins(p.hearts_count)}</span>
                        <span>🪙 {_fmtCoins(p.gift_coins)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* RIGHT: Country leaderboard */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24">
            <h2 className="font-display text-2xl flex items-center gap-2 mb-4">
              <Globe2 className="w-5 h-5 text-white/85" /> Country leaderboard
            </h2>
            <p className="text-xs text-white/45 mb-4 leading-relaxed">
              Hearts + gifts + Judge&apos;s Picks combined — last {data.window_days} days of talent contests.
            </p>
            {country_leaderboard.length === 0 ? (
              <div className="p-5 rounded-xl border border-dashed border-white/10 text-xs text-white/55 text-center">
                No talent entries yet. Be the first to enter and put your country on the board.
              </div>
            ) : (
              <ol className="space-y-2" data-testid="plyx-talent-country-leaderboard">
                {country_leaderboard.slice(0, 12).map((c) => (
                  <li
                    key={c.country}
                    data-testid={`plyx-talent-country-${c.country.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center gap-3 p-3 rounded-lg border border-white/8 bg-white/[0.025]"
                  >
                    <RankBadge rank={c.rank} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{c.country}</div>
                      <div className="text-[11px] text-white/45 flex gap-2.5">
                        <span>{c.entries} entries</span>
                        <span>♥ {_fmtCoins(c.hearts)}</span>
                        <span>🪙 {_fmtCoins(c.gift_coins)}</span>
                        {c.judges_picks > 0 && <span className="text-[#FFB42D]">🏆 {c.judges_picks}</span>}
                      </div>
                    </div>
                    <div className="text-sm font-bold text-white/85 tabular-nums">{_fmtCoins(c.score)}</div>
                  </li>
                ))}
              </ol>
            )}
            <Link
              to="/plyx/leaderboard"
              data-testid="plyx-talent-creators-leaderboard-link"
              className="mt-4 inline-flex items-center gap-1 text-xs text-white/55 hover:text-white"
            >
              Creator leaderboard <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default PlyxTalent;
